import React, { useState, useCallback, useMemo } from "react";
import { Popover } from "antd";
import { LeftOutlined, RightOutlined, CaretDownOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

interface PeriodSelectorProps {
  selectedMonths: string[];
  onMonthsChange: (months: string[]) => void;
  disabled?: boolean;
}

function parseKey(key: string): { year: number; monthIdx: number } {
  const [y, m] = key.split("-").map(Number);
  return { year: y, monthIdx: m - 1 };
}

function toKey(year: number, monthIdx: number): string {
  return `${year}-${String(monthIdx + 1).padStart(2, "0")}`;
}

function sortedMonths(months: string[]): string[] {
  return [...months].sort();
}

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedMonths,
  onMonthsChange,
  disabled = false,
}) => {
  const today = dayjs();
  const todayKey = today.format("YYYY-MM");

  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => {
    const sorted = sortedMonths(selectedMonths);
    return sorted.length > 0 ? parseInt(sorted[0].slice(0, 4)) : today.year();
  });

  // Shift every selected month by +1 or -1, preserving gaps
  const handleShift = useCallback(
    (dir: 1 | -1) => {
      if (disabled || selectedMonths.length === 0) return;
      const next = selectedMonths.map((key) => {
        const { year, monthIdx } = parseKey(key);
        let nm = monthIdx + dir;
        let ny = year;
        if (nm < 0) { nm = 11; ny--; }
        if (nm > 11) { nm = 0; ny++; }
        return toKey(ny, nm);
      });
      onMonthsChange(next);
      const sorted = sortedMonths(next);
      if (sorted.length > 0) setPickerYear(parseInt(sorted[0].slice(0, 4)));
    },
    [selectedMonths, onMonthsChange, disabled],
  );

  // Toggle a month in the grid; always keep at least one selected
  const handleToggle = useCallback(
    (monthIdx: number) => {
      const key = toKey(pickerYear, monthIdx);
      if (selectedMonths.includes(key)) {
        if (selectedMonths.length > 1) {
          onMonthsChange(selectedMonths.filter((m) => m !== key));
        }
      } else {
        onMonthsChange([...selectedMonths, key]);
      }
    },
    [selectedMonths, onMonthsChange, pickerYear],
  );

  const displayLabel = useMemo(() => {
    const sorted = sortedMonths(selectedMonths);
    if (sorted.length === 0) return { head: "—", range: "—" };
    if (sorted.length === 1) {
      const d = dayjs(sorted[0]);
      return {
        head: d.format("MMM YYYY"),
        range: `01.${d.format("MM.YYYY")} – ${d.daysInMonth()}.${d.format("MM.YYYY")}`,
      };
    }
    const yearSet = new Set(sorted.map((s) => s.slice(0, 4)));
    const years = Array.from(yearSet);
    const parts = sorted.map((s) => {
      const d = dayjs(s);
      return years.length > 1 ? d.format("MMM 'YY") : d.format("MMM");
    });
    return {
      head: `${sorted.length} meseca`,
      range: parts.join(" · ") + (years.length === 1 ? " " + years[0] : ""),
    };
  }, [selectedMonths]);

  const MONTHS_SHORT = Array.from({ length: 12 }, (_, i) =>
    dayjs().month(i).format("MMM"),
  );

  const popContent = (
    <div style={{ width: 276 }}>
      {/* Year nav */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <button style={ynav} onClick={() => setPickerYear((y) => y - 1)}>
          ‹
        </button>
        <span
          style={{ fontWeight: 600, fontSize: 14, fontVariantNumeric: "tabular-nums" }}
        >
          {pickerYear}
        </span>
        <button style={ynav} onClick={() => setPickerYear((y) => y + 1)}>
          ›
        </button>
      </div>

      {/* Month grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 6,
        }}
      >
        {MONTHS_SHORT.map((name, idx) => {
          const key = toKey(pickerYear, idx);
          const sel = selectedMonths.includes(key);
          const isToday = key === todayKey;
          return (
            <button
              key={idx}
              onClick={() => handleToggle(idx)}
              style={{
                height: 36,
                borderRadius: 8,
                border: sel ? "1.5px solid #6366f1" : "1px solid #e5e7eb",
                background: sel ? "#eef2ff" : "#fff",
                color: sel ? "#4f46e5" : "#374151",
                fontWeight: sel ? 650 : 500,
                fontSize: 12.5,
                cursor: "pointer",
                position: "relative",
                transition: "all 0.12s",
              }}
            >
              {name}
              {isToday && (
                <span
                  style={{
                    position: "absolute",
                    bottom: 4,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: "currentColor",
                    opacity: 0.7,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 12,
          paddingTop: 12,
          borderTop: "1px solid #f0f0f0",
        }}
      >
        <span style={{ flex: 1, fontSize: 11.5, color: "#9ca3af" }}>
          {selectedMonths.length === 1
            ? "1 mesec izabran"
            : `${selectedMonths.length} meseca izabrano`}
        </span>
        <button
          style={footBtn}
          onClick={() => {
            onMonthsChange([todayKey]);
            setPickerYear(today.year());
          }}
        >
          Resetuj
        </button>
        <button
          style={{ ...footBtn, background: "#111827", color: "#fff", border: "none", fontWeight: 600 }}
          onClick={() => setOpen(false)}
        >
          Gotovo
        </button>
      </div>
    </div>
  );

  const borderColor = open ? "#6366f1" : "#d9d9d9";
  const shadow = open ? "0 0 0 2px rgba(99,102,241,0.15)" : "none";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        height: 36,
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        background: "#fff",
        boxShadow: shadow,
        transition: "all 0.15s",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <button
        style={arrowBtn}
        onClick={() => handleShift(-1)}
        disabled={disabled}
        title="Prethodni"
      >
        <LeftOutlined style={{ fontSize: 10 }} />
      </button>

      <Popover
        open={open}
        onOpenChange={(v) => !disabled && setOpen(v)}
        content={popContent}
        trigger="click"
        placement="bottomLeft"
        arrow={false}
        overlayInnerStyle={{
          borderRadius: 12,
          padding: 14,
          border: "1px solid #e5e7eb",
          boxShadow: "0 20px 48px -12px rgba(40,30,60,0.28)",
        }}
      >
        <button
          disabled={disabled}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderLeft: "1px solid #f0f0f0",
            borderRight: "1px solid #f0f0f0",
            background: "transparent",
            cursor: "pointer",
            padding: "0 14px",
            minWidth: 160,
            lineHeight: 1.3,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 13,
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {displayLabel.head}
            <CaretDownOutlined
              style={{
                fontSize: 9,
                color: "#9ca3af",
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.15s",
              }}
            />
          </span>
          <span
            style={{
              fontSize: 10,
              color: "#9ca3af",
              fontFamily: "monospace",
              letterSpacing: "-0.01em",
            }}
          >
            {displayLabel.range}
          </span>
        </button>
      </Popover>

      <button
        style={arrowBtn}
        onClick={() => handleShift(1)}
        disabled={disabled}
        title="Sledeći"
      >
        <RightOutlined style={{ fontSize: 10 }} />
      </button>
    </div>
  );
};

const arrowBtn: React.CSSProperties = {
  width: 32,
  height: 34,
  border: "none",
  background: "transparent",
  color: "#6b7280",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  borderRadius: 7,
  flexShrink: 0,
};

const ynav: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 7,
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#6b7280",
  fontSize: 18,
  lineHeight: 1,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

const footBtn: React.CSSProperties = {
  height: 28,
  padding: "0 10px",
  borderRadius: 7,
  border: "1px solid #e5e7eb",
  background: "#fff",
  fontSize: 12,
  cursor: "pointer",
  color: "#6b7280",
};

export default PeriodSelector;
